import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, auth } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Sparkles, Crown, Loader2, Send, BookOpen, Camera, GraduationCap, Library, Check, Play, Wand2 } from "lucide-react";
import { canUse, consume, getRemaining, isGuest } from "@/lib/guest";
import GuestBanner from "@/components/GuestBanner";
import Paywall from "@/components/Paywall";
import { toast } from "sonner";

const LEVELS = [
  { key: "debutant", fr: "Débutant", en: "Beginner" },
  { key: "amateur", fr: "Amateur", en: "Wine lover" },
  { key: "professionnel", fr: "Professionnel", en: "Professional" },
  { key: "expert", fr: "Expert", en: "Expert" },
];

const SUGGESTED_QUESTIONS = [
  "Pourquoi les sols calcaires de Chablis donnent des Chardonnay si minéraux ?",
  "Quelle est la différence entre Grand Cru et Premier Cru en Bourgogne ?",
  "Comment l'altitude influe sur la maturation des raisins ?",
  "Qu'est-ce qu'un climat au sens du cahier des charges de Bourgogne ?",
  "Pourquoi le Pinot Noir pousse mal dans le Bordelais ?",
];

export default function AudreyAI() {
  const { lang } = useT();
  const navigate = useNavigate();
  const [tab, setTab] = useState("terroir"); // 'terroir' | 'educational' | 'photo'
  const [me, setMe] = useState(null);
  const [level, setLevel] = useState("amateur");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [topic, setTopic] = useState("");
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paywall, setPaywall] = useState(null); // { reason, cost, credits }
  const [remaining, setRemaining] = useState(getRemaining("chat"));
  // Microlearning state
  const [series, setSeries] = useState([]);
  const [activeSeries, setActiveSeries] = useState(null);
  const [todayReco, setTodayReco] = useState(null);
  const [microLesson, setMicroLesson] = useState(null);
  const [customTopic, setCustomTopic] = useState("");
  const bottomRef = useRef(null);

  const loadMicro = async () => {
    try {
      const [{ data: list }, { data: today }] = await Promise.all([
        api.get("/microlearning/series"),
        api.get("/microlearning/today"),
      ]);
      setSeries(list);
      setTodayReco(today);
    } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plans/me");
        setMe(data);
      } catch {}
    })();
    loadMicro();
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [answer, lesson]);

  const isPremium = me?.plan?.audrey_ai === true;

  const handleTerroir = async (e) => {
    e?.preventDefault();
    if (!question.trim()) return;
    // Guest quota gate (free tier uses chat quota)
    if (!isPremium && !canUse("chat")) {
      setPaywall({ reason: "quota" });
      setRemaining(0);
      return;
    }
    setLoading(true);
    setAnswer(null);
    setPaywall(null);
    try {
      const { data } = await api.post("/audrey-ai/terroir-expert", { question, level, lang });
      setAnswer(data);
      if (!isPremium) {
        const c = consume("chat");
        setRemaining(c.remaining === Infinity ? null : c.remaining);
      } else {
        // Refresh credits balance
        api.get("/plans/me").then(({ data }) => setMe(data)).catch(() => {});
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "insufficient_credits") {
        setPaywall({ reason: "credits", cost: detail.cost, credits: detail.credits });
      } else {
        toast.error(lang === "fr" ? "geoVINUM AI est momentanément indisponible." : "geoVINUM AI is unavailable.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEducational = async (e) => {
    e?.preventDefault();
    if (!topic.trim()) return;
    if (!isPremium && !canUse("chat")) {
      setPaywall({ reason: "quota" });
      setRemaining(0);
      return;
    }
    setLoading(true);
    setLesson(null);
    setPaywall(null);
    try {
      const { data } = await api.post("/audrey-ai/educational", { topic, level, lang });
      setLesson(data.lesson);
      if (!isPremium) {
        const c = consume("chat");
        setRemaining(c.remaining === Infinity ? null : c.remaining);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "insufficient_credits") {
        setPaywall({ reason: "credits", cost: detail.cost, credits: detail.credits });
      } else {
        toast.error(lang === "fr" ? "Génération impossible." : "Generation failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  // ---- Microlearning ----
  const startSeries = async (sid, lessonIndex = null) => {
    if (!isPremium && !canUse("chat")) {
      setPaywall({ reason: "quota" });
      setRemaining(0);
      return;
    }
    setLoading(true);
    setMicroLesson(null);
    try {
      const { data: s } = await api.get(`/microlearning/series/${sid}`);
      setActiveSeries(s);
      const idx = lessonIndex != null ? lessonIndex : (s.progress?.last_lesson_index ?? 0);
      const { data } = await api.post("/microlearning/lesson", { series_id: sid, lesson_index: idx, lang });
      setMicroLesson(data);
      if (!isPremium) {
        const c = consume("chat");
        setRemaining(c.remaining === Infinity ? null : c.remaining);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "insufficient_credits") setPaywall({ reason: "credits", cost: detail.cost, credits: detail.credits });
      else toast.error(lang === "fr" ? "Chargement impossible" : "Loading failed");
    } finally {
      setLoading(false);
    }
  };

  const completeLesson = async () => {
    if (!microLesson || !activeSeries) return;
    if (!auth.getToken()) { toast.error(lang === "fr" ? "Connectez-vous pour sauvegarder votre progression" : "Sign in to save progress"); return; }
    try {
      await api.post("/microlearning/complete", { series_id: microLesson.series_id, lesson_index: microLesson.lesson_index });
      toast.success(lang === "fr" ? "Leçon validée ✓" : "Lesson completed ✓");
      await loadMicro();
    } catch { toast.error("Erreur"); }
  };

  const nextLesson = async () => {
    if (!microLesson || !activeSeries) return;
    const next = microLesson.lesson_index + 1;
    if (next >= activeSeries.lessons.length) {
      toast.success(lang === "fr" ? "Cursus terminé 🎓" : "Course completed 🎓");
      setMicroLesson(null);
      setActiveSeries(null);
      return;
    }
    await startSeries(microLesson.series_id, next);
  };

  const generateCustomSeries = async () => {
    if (!customTopic.trim()) return;
    if (!isPremium && !canUse("chat")) { setPaywall({ reason: "quota" }); setRemaining(0); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/microlearning/series/generate", { topic: customTopic, level, lesson_count: 6, lang });
      setCustomTopic("");
      await loadMicro();
      toast.success(lang === "fr" ? `Cursus créé : « ${data.title} »` : `Series created: "${data.title}"`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.error === "insufficient_credits") setPaywall({ reason: "credits", cost: detail.cost, credits: detail.credits });
      else toast.error(lang === "fr" ? "Génération impossible" : "Generation failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-10 py-10 md:py-14">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div className="max-w-2xl flex items-start gap-4">
          <img
            src="/brand/audrey-ai.png"
            alt="geoVINUM AI"
            className="w-20 h-20 md:w-24 md:h-24 rounded-2xl object-cover shadow-md hidden sm:block shrink-0"
            data-testid="audrey-hero-avatar"
          />
          <div>
            <div className="inline-flex items-center gap-2">
              <span className="chip-pink">geoVINUM AI</span>
              {isPremium && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#1F3A5F] text-white">
                  <Crown className="w-3 h-3" /> {me?.plan?.name}
                </span>
              )}
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-3 leading-[1.05]">
              {lang === "fr" ? "Votre experte du terroir" : "Your terroir expert"}
            </h1>
            <p className="text-[#4A5568] mt-2">
              {lang === "fr"
                ? "Sols, climats, géologie, parcelles, cépages, viticulture, vinification, histoire — adapté à votre niveau."
                : "Soils, climates, geology, plots, grapes, viticulture, vinification, history — tailored to your level."}
            </p>
          </div>
        </div>
        {me && !me.is_guest && (
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider font-bold text-[#8B95A1]">
              {lang === "fr" ? "Crédits" : "Credits"}
            </div>
            <div className="font-display text-2xl text-[#E5276A]">{me.credits ?? 0}</div>
            <Link to="/pricing" className="text-xs underline text-[#1F3A5F] font-bold">{lang === "fr" ? "Recharger" : "Top up"}</Link>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-8 flex flex-wrap gap-2" role="tablist">
        {[
          { key: "terroir", icon: BookOpen, label_fr: "Terroir Expert", label_en: "Terroir Expert" },
          { key: "educational", icon: GraduationCap, label_fr: "Mode éducatif", label_en: "Educational mode" },
          { key: "microlearning", icon: Library, label_fr: "Microlearning", label_en: "Microlearning" },
          { key: "photo", icon: Camera, label_fr: "Photo Explain", label_en: "Photo Explain" },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              data-testid={`audrey-tab-${t.key}`}
              onClick={() => t.key === "photo" ? navigate("/explorer") : setTab(t.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-colors ${
                active ? "bg-[#1F3A5F] text-white" : "bg-white border border-[#1F3A5F]/15 text-[#1F3A5F] hover:border-[#E5276A]"
              }`}
            >
              <Icon className="w-4 h-4" /> {lang === "fr" ? t.label_fr : t.label_en}
            </button>
          );
        })}
      </div>

      {/* Level selector */}
      <div className="mt-5 rounded-2xl bg-white border border-[#1F3A5F]/12 p-4">
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8B95A1]">
          {lang === "fr" ? "Niveau pédagogique" : "Pedagogical level"}
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="audrey-level-selector">
          {LEVELS.map((l) => (
            <button
              key={l.key}
              data-testid={`audrey-level-${l.key}`}
              onClick={() => setLevel(l.key)}
              className={`px-3 py-1.5 rounded-full text-[12px] font-bold border transition-colors ${
                level === l.key
                  ? "bg-[#E5276A] text-white border-[#E5276A]"
                  : "bg-white text-[#1F3A5F] border-[#1F3A5F]/15 hover:border-[#E5276A]"
              }`}
            >
              {lang === "fr" ? l.fr : l.en}
            </button>
          ))}
        </div>
      </div>

      {isGuest() && <div className="mt-5"><GuestBanner feature="chat" remaining={remaining} /></div>}

      {paywall && (
        <div className="mt-5"><Paywall feature={tab} reason={paywall.reason} cost={paywall.cost} credits={paywall.credits} /></div>
      )}

      {/* TAB: Terroir Expert */}
      {tab === "terroir" && (
        <div className="mt-6 space-y-4">
          <form onSubmit={handleTerroir} className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-4 md:p-5">
            <textarea
              data-testid="audrey-terroir-question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={lang === "fr" ? "Posez votre question à geoVINUM AI…" : "Ask geoVINUM AI…"}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A] resize-none"
            />
            <div className="mt-3 flex justify-between items-center flex-wrap gap-3">
              <div className="flex flex-wrap gap-1.5">
                {SUGGESTED_QUESTIONS.slice(0, 3).map((q) => (
                  <button
                    key={q}
                    type="button"
                    onClick={() => setQuestion(q)}
                    className="px-2 py-1 rounded-full text-[11px] font-medium bg-[#FBF7F3] text-[#1F3A5F] border border-[#1F3A5F]/10 hover:border-[#E5276A]"
                  >
                    {q.length > 50 ? q.slice(0, 50) + "…" : q}
                  </button>
                ))}
              </div>
              <button
                type="submit"
                data-testid="audrey-terroir-submit"
                disabled={loading}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-60"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {lang === "fr" ? "Demander à geoVINUM AI" : "Ask geoVINUM AI"}
              </button>
            </div>
          </form>

          {answer && (
            <div className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-5 md:p-6" data-testid="audrey-terroir-answer">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-[#E5276A]">
                <Sparkles className="w-3.5 h-3.5" /> {lang === "fr" ? "Réponse · niveau" : "Answer · level"} {answer.level}
              </div>
              <article className="mt-3 prose-narration">
                {(answer.answer || "").split(/\n\n+/).map((p, i) => (
                  <p key={i} className="text-[#1F3A5F] text-[15px] leading-[1.7] mb-3 font-[Lato]">{p}</p>
                ))}
              </article>
            </div>
          )}
        </div>
      )}

      {/* TAB: Educational */}
      {tab === "educational" && (
        <div className="mt-6 space-y-4">
          <form onSubmit={handleEducational} className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-4 md:p-5">
            <input
              data-testid="audrey-edu-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={lang === "fr" ? "Quel sujet voulez-vous apprendre ?" : "What do you want to learn?"}
              className="w-full px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]"
            />
            <div className="mt-3 flex flex-wrap gap-1.5">
              {["Le terroir", "Les climats de Bourgogne", "La fermentation malolactique", "Les sols volcaniques"].map((s) => (
                <button key={s} type="button" onClick={() => setTopic(s)} className="px-2 py-1 rounded-full text-[11px] font-medium bg-[#FBF7F3] text-[#1F3A5F] border border-[#1F3A5F]/10 hover:border-[#E5276A]">
                  {s}
                </button>
              ))}
            </div>
            <button type="submit" data-testid="audrey-edu-submit" disabled={loading} className="mt-4 inline-flex items-center gap-2 px-5 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <GraduationCap className="w-4 h-4" />}
              {lang === "fr" ? "Générer la leçon" : "Generate lesson"}
            </button>
          </form>

          {lesson && (
            <div className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-5 md:p-6" data-testid="audrey-edu-lesson">
              <h2 className="font-display text-2xl text-[#1F3A5F]">{lesson.title}</h2>
              {lesson.subtitle && <p className="text-sm text-[#8B95A1] mt-1">{lesson.subtitle}</p>}
              <article className="mt-4 prose-narration">
                {(lesson.body || "").split(/\n\n+/).map((p, i) => (
                  <p key={i} className="text-[#1F3A5F] text-[15px] leading-[1.7] mb-3 font-[Lato]">{p}</p>
                ))}
              </article>
              {lesson.key_takeaway && (
                <div className="mt-4 rounded-xl bg-[#FCE4ED] border-l-[3px] border-[#E5276A] p-4 text-sm text-[#1F3A5F]">
                  <b>{lang === "fr" ? "À retenir : " : "Takeaway: "}</b>{lesson.key_takeaway}
                </div>
              )}
              {(lesson.vocabulary?.length > 0) && (
                <div className="mt-4">
                  <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#E5276A]">{lang === "fr" ? "Vocabulaire" : "Vocabulary"}</h3>
                  <dl className="mt-2 grid sm:grid-cols-2 gap-2">
                    {lesson.vocabulary.map((v, i) => (
                      <div key={i} className="rounded-lg bg-[#FBF7F3] p-3 text-sm">
                        <dt className="font-bold text-[#1F3A5F]">{v.term}</dt>
                        <dd className="text-[#4A5568] text-xs mt-0.5">{v.definition}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              {(lesson.next_steps?.length > 0) && (
                <div className="mt-4">
                  <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#E5276A]">{lang === "fr" ? "Pour aller plus loin" : "Go further"}</h3>
                  <ul className="mt-2 text-sm text-[#4A5568] space-y-1">
                    {lesson.next_steps.map((s, i) => <li key={i} className="flex gap-2"><span className="text-[#E5276A]">·</span>{s}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* TAB: Microlearning */}
      {tab === "microlearning" && (
        <div className="mt-6 space-y-6" data-testid="audrey-microlearning">
          {/* Aujourd'hui */}
          {todayReco && !microLesson && (
            <div className="rounded-2xl border border-[#1F3A5F]/12 bg-gradient-to-br from-[#FCE4ED] to-white p-5 md:p-6">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div>
                  <div className="text-[11px] uppercase tracking-wider font-bold text-[#E5276A]">
                    {todayReco.kind === "continue" ? (lang === "fr" ? "Reprenez où vous étiez" : "Resume where you left off") : (lang === "fr" ? "Votre leçon du jour" : "Today's lesson")}
                  </div>
                  <h3 className="font-display text-xl md:text-2xl text-[#1F3A5F] mt-1">{todayReco.lesson_title}</h3>
                  <p className="text-xs text-[#8B95A1] mt-1">{lang === "fr" ? "Série" : "Series"} · {todayReco.series_title}</p>
                </div>
                <button
                  data-testid="audrey-micro-start-today"
                  onClick={() => startSeries(todayReco.series_id, todayReco.lesson_index)}
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-60"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  {lang === "fr" ? "Démarrer" : "Start"}
                </button>
              </div>
            </div>
          )}

          {/* Active lesson */}
          {microLesson && activeSeries && (
            <div className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-5 md:p-6" data-testid="audrey-micro-active-lesson">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-[#8B95A1]">{activeSeries.title} · {microLesson.lesson_index + 1}/{activeSeries.lessons.length}</div>
                  <h2 className="font-display text-2xl text-[#1F3A5F]">{microLesson.content.title}</h2>
                </div>
                <button onClick={() => { setMicroLesson(null); setActiveSeries(null); }} className="text-xs text-[#8B95A1] hover:text-[#E5276A]">✕ {lang === "fr" ? "Fermer" : "Close"}</button>
              </div>
              <article className="mt-4 prose-narration" data-testid="audrey-micro-lesson-body">
                {(microLesson.content.body || "").split(/\n\n+/).map((p, i) => (
                  <p key={i} className="text-[#1F3A5F] text-[15px] leading-[1.7] mb-3 font-[Lato]">{p}</p>
                ))}
              </article>
              {microLesson.content.key_takeaway && (
                <div className="rounded-xl bg-[#FCE4ED] border-l-[3px] border-[#E5276A] p-3 text-sm text-[#1F3A5F] mt-2">
                  <b>{lang === "fr" ? "À retenir : " : "Takeaway: "}</b>{microLesson.content.key_takeaway}
                </div>
              )}
              {(microLesson.content.vocabulary?.length > 0) && (
                <div className="mt-4">
                  <h3 className="text-[11px] uppercase tracking-wider font-bold text-[#E5276A]">{lang === "fr" ? "Vocabulaire" : "Vocabulary"}</h3>
                  <dl className="mt-2 grid sm:grid-cols-2 gap-2">
                    {microLesson.content.vocabulary.map((v, i) => (
                      <div key={i} className="rounded-lg bg-[#FBF7F3] p-3 text-sm">
                        <dt className="font-bold text-[#1F3A5F]">{v.term}</dt>
                        <dd className="text-[#4A5568] text-xs mt-0.5">{v.definition}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              {microLesson.content.quiz && (
                <div className="mt-4 rounded-xl bg-[#FBF7F3] p-4">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-[#1F3A5F]">{lang === "fr" ? "Mini-quiz" : "Mini-quiz"}</div>
                  <p className="font-bold text-[#1F3A5F] mt-1">{microLesson.content.quiz.question}</p>
                  <ul className="mt-2 space-y-1.5 text-sm">
                    {microLesson.content.quiz.choices.map((c, i) => (
                      <li key={i} className="text-[#4A5568]">
                        <span className="font-bold text-[#E5276A] mr-1">{String.fromCharCode(65 + i)}.</span>{c}
                        {i === microLesson.content.quiz.answer_index && <span className="ml-2 text-green-700 font-bold">✓</span>}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-[#8B95A1] italic mt-2">{microLesson.content.quiz.explain}</p>
                </div>
              )}
              <div className="mt-5 flex gap-2 flex-wrap">
                <button data-testid="audrey-micro-complete" onClick={completeLesson} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1F3A5F] text-white text-sm font-bold hover:bg-[#2A4870]">
                  <Check className="w-4 h-4" /> {lang === "fr" ? "Valider cette leçon" : "Mark complete"}
                </button>
                <button data-testid="audrey-micro-next" onClick={nextLesson} disabled={loading} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-60">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  {microLesson.lesson_index + 1 < activeSeries.lessons.length ? (lang === "fr" ? "Leçon suivante" : "Next lesson") : (lang === "fr" ? "Terminer" : "Finish")}
                </button>
              </div>
            </div>
          )}

          {/* Custom AI series generator */}
          {!microLesson && (
            <div className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-5">
              <div className="flex items-center gap-2 text-[#E5276A]">
                <Wand2 className="w-4 h-4" />
                <h3 className="font-bold text-sm uppercase tracking-wider">{lang === "fr" ? "Demander à geoVINUM AI de créer un cursus" : "Ask geoVINUM AI to build a course"}</h3>
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                <input
                  data-testid="audrey-micro-custom-topic"
                  value={customTopic}
                  onChange={(e) => setCustomTopic(e.target.value)}
                  placeholder={lang === "fr" ? "Ex : Les vins de Sicile, La Champagne pour débutants…" : "e.g. Sicilian wines, Champagne for beginners…"}
                  className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]"
                />
                <button
                  data-testid="audrey-micro-custom-submit"
                  onClick={generateCustomSeries}
                  disabled={loading || !customTopic.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-60"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {lang === "fr" ? "Générer le cursus" : "Generate course"}
                </button>
              </div>
              <p className="text-xs text-[#8B95A1] mt-2">{lang === "fr" ? `Niveau : ${level}. 6 leçons générées, à la demande.` : `Level: ${level}. 6 lessons, on demand.`}</p>
            </div>
          )}

          {/* Curated + AI series list */}
          {!microLesson && (
            <div>
              <h3 className="font-display text-xl text-[#1F3A5F]">{lang === "fr" ? "Cursus disponibles" : "Available courses"}</h3>
              <div className="mt-3 grid md:grid-cols-2 gap-4" data-testid="audrey-micro-series-list">
                {series.map((s) => {
                  const pct = s.progress ? Math.round((s.progress.completed_count / s.lesson_count) * 100) : 0;
                  return (
                    <div key={s.id} className="rounded-2xl border border-[#1F3A5F]/12 bg-white p-5 hover:border-[#E5276A] transition-colors" data-testid={`audrey-micro-series-${s.id}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <h4 className="font-bold text-[#1F3A5F]">{s.title}</h4>
                        {s.ai_generated && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-[#FCE4ED] text-[#E5276A]">IA</span>
                        )}
                      </div>
                      <p className="text-xs text-[#4A5568] mt-1 line-clamp-2">{s.description}</p>
                      <div className="mt-2 flex items-center gap-3 text-[11px] text-[#8B95A1]">
                        <span>{s.lesson_count} {lang === "fr" ? "leçons" : "lessons"}</span>
                        <span>· {s.duration_min} min</span>
                        <span>· {s.level}</span>
                      </div>
                      {s.progress && s.progress.completed_count > 0 && (
                        <div className="mt-3">
                          <div className="h-1.5 rounded-full bg-[#FBF7F3] overflow-hidden">
                            <div className="h-full bg-[#E5276A]" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="text-[10px] text-[#8B95A1] mt-1">{s.progress.completed_count}/{s.lesson_count} · {pct}%</div>
                        </div>
                      )}
                      <button
                        data-testid={`audrey-micro-start-${s.id}`}
                        onClick={() => startSeries(s.id)}
                        disabled={loading}
                        className="mt-4 w-full inline-flex justify-center items-center gap-2 px-3 py-2 rounded-xl bg-[#1F3A5F] text-white text-xs font-bold hover:bg-[#2A4870] disabled:opacity-60"
                      >
                        <Play className="w-3.5 h-3.5" />
                        {s.progress?.completed_count > 0 ? (lang === "fr" ? "Continuer" : "Continue") : (lang === "fr" ? "Démarrer" : "Start")}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
